import { api, AUTH_URL } from './client';

export async function getMe(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me`, options);
}

export async function getMeExtended(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me/extended`, options);
}

export async function switchTenant(tenantId, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/switch-tenant/${tenantId}`, {}, options);
}

export async function logout(options = {}) {
  return api.post(`${AUTH_URL}/auth/logout`, {}, options);
}

// Build a redirect_to param so shared auth services (e.g. staging-auth) know which
// frontend to send the user back to after OAuth. This lets multiple environments
// (staging2, staging3, etc.) share a single registered OAuth callback URL.
function redirectToParam() {
  // Always pass current origin as redirect_to so shared auth services (e.g. staging-auth)
  // know which frontend to send the user back to after OAuth. Auth service validates
  // the URL is on the same base domain before accepting it.
  return `?redirect_to=${encodeURIComponent(window.location.origin)}`;
}

export function redirectToLogin() {
  window.location.href = `${AUTH_URL}/auth/google${redirectToParam()}`;
}

export function redirectToGoogleLogin() {
  window.location.href = `${AUTH_URL}/auth/google${redirectToParam()}`;
}

export function redirectToSalesforceLogin() {
  window.location.href = `${AUTH_URL}/auth/salesforce${redirectToParam()}`;
}

// Redirect to Salesforce OAuth to add a new data source to the current tenant
export function redirectToSalesforceAddDataSource(tenantId) {
  const p = redirectToParam();
  window.location.href = `${AUTH_URL}/auth/salesforce${p}&add_to_tenant=${encodeURIComponent(tenantId)}`;
}

// Redirect to Salesforce OAuth with return URL (for re-authentication flows)
export function redirectToSalesforceReauth(returnPath) {
  const encodedPath = encodeURIComponent(returnPath || window.location.pathname);
  const p = redirectToParam();
  window.location.href = `${AUTH_URL}/auth/salesforce${p}&return_to=${encodedPath}`;
}

// HubSpot OAuth functions
export function redirectToHubSpotLogin() {
  window.location.href = `${AUTH_URL}/auth/hubspot${redirectToParam()}`;
}

// Redirect to HubSpot OAuth to add a new data source to the current tenant
export function redirectToHubSpotAddDataSource(tenantId) {
  const p = redirectToParam();
  window.location.href = `${AUTH_URL}/auth/hubspot${p}&add_to_tenant=${encodeURIComponent(tenantId)}`;
}

// Redirect to HubSpot OAuth with return URL and tenant context (for re-authentication flows).
// tenantId must be provided so the callback uses the add_to_tenant path and updates the
// existing token instead of creating a new hubspot-<portalId> tenant.
export function redirectToHubSpotReauth(returnPath, tenantId) {
  const encodedPath = encodeURIComponent(returnPath || window.location.pathname);
  const p = redirectToParam();
  const tenantParam = tenantId ? `&add_to_tenant=${encodeURIComponent(tenantId)}` : '';
  window.location.href = `${AUTH_URL}/auth/hubspot${p}&return_to=${encodedPath}${tenantParam}`;
}

// Invite management
export async function listInvites(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/invites`, options);
}

export async function createInvite(email, role, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/invites`, { email, role }, options);
}

export async function deleteInvite(inviteId, options = {}) {
  return api.delete(`${AUTH_URL}/api/v1/invites/${inviteId}`, options);
}

// Profile management
export async function getProfile(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me/profile`, options);
}

export async function updateProfile(profileData, options = {}) {
  return api.put(`${AUTH_URL}/api/v1/me/profile`, profileData, options);
}

// Tenant status (for users without tenants)
export async function getTenantStatus(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me/tenant-status`, options);
}

// Tenant integrations (admin only)
export async function getTenantIntegrations(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/tenant/integrations`, options);
}

// Per-user Salesforce token management

// Get all Salesforce tokens for the current tenant with user info
export async function getTenantSalesforceTokens(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/tenant/salesforce/tokens`, options);
}

// Set the default Salesforce token for the tenant (admin only)
export async function setDefaultSalesforceToken(tokenId, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/tenant/salesforce/tokens/set-default`, { token_id: tokenId }, options);
}

// Revoke a user's Salesforce token (admin only)
export async function revokeUserSalesforceToken(tokenId, options = {}) {
  return api.delete(`${AUTH_URL}/api/v1/tenant/salesforce/tokens/${tokenId}`, options);
}

// Get current user's Salesforce token for the active tenant
export async function getMyToken(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me/salesforce/token`, options);
}

// =============================================================================
// ONBOARDING APIs
// =============================================================================

// Get onboarding status (determines which onboarding flow to show)
export async function getOnboardingStatus(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/me/onboarding-status`, options);
}

// Mark a tutorial as completed
export async function completeTutorial(tutorialKey, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/me/tutorials/${tutorialKey}/complete`, {}, options);
}

// Mark a tutorial as skipped
export async function skipTutorial(tutorialKey, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/me/tutorials/${tutorialKey}/skip`, {}, options);
}

// Mark onboarding as complete
export async function completeOnboarding(options = {}) {
  return api.post(`${AUTH_URL}/api/v1/me/onboarding/complete`, {}, options);
}

// Create an access request for a tenant
export async function createAccessRequest(tenantId, salesforceOrgId, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/me/access-request`, {
    tenant_id: tenantId,
    salesforce_org_id: salesforceOrgId,
  }, options);
}

// Get pending access requests (admin only)
export async function getPendingRequests(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/tenant/pending-requests`, options);
}

// Approve an access request (admin only)
export async function approveAccessRequest(requestId, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/tenant/pending-requests/${requestId}/approve`, {}, options);
}

// Reject an access request (admin only)
export async function rejectAccessRequest(requestId, reason = '', options = {}) {
  return api.post(`${AUTH_URL}/api/v1/tenant/pending-requests/${requestId}/reject`, { reason }, options);
}

// =============================================================================
// DATA SOURCES APIs (Multi-org support)
// =============================================================================

// List all data sources for the current tenant
export async function listDataSources(options = {}) {
  return api.get(`${AUTH_URL}/api/v1/tenant/data-sources`, options);
}

// Get a single data source by ID
export async function getDataSource(dataSourceId, options = {}) {
  return api.get(`${AUTH_URL}/api/v1/tenant/data-sources/${dataSourceId}`, options);
}

// Delete a data source (admin only)
export async function deleteDataSource(dataSourceId, options = {}) {
  return api.delete(`${AUTH_URL}/api/v1/tenant/data-sources/${dataSourceId}`, options);
}

// Create a new BYOD (file-type) data source (admin only)
export async function createDataSource(body, options = {}) {
  return api.post(`${AUTH_URL}/api/v1/tenant/data-sources`, body, options);
}
